import { Controller } from '@hotwired/stimulus';

// Keyboard navigation over the Quick Actions rows, plus type-to-search where a
// search input renders. The Pages section is search-only: it stays hidden until a
// query matches one.
export default class extends Controller {
  static targets = [
    'searchInput',
    'results',
    'action',
    'category',
    'page',
    'pagesGroup',
    'noResults',
    'emptyState',
  ];

  connect() {
    this.highlightedIndex = -1;
    this.#identifyRows();

    // Only the searchable palette can reveal a search-only row, so only there
    // does a category header's visibility have to be derived up front.
    if (this.hasSearchInputTarget) {
      this.#updateCategoryVisibility();
    }

    const modal = this.element.closest('.modal');
    if (modal && this.hasSearchInputTarget) {
      // Covers a palette that connects into an already-shown modal, where
      // `shown.bs.modal` has fired before this controller existed.
      this.searchInputTarget.focus();

      // Bootstrap's focus trap parks focus on the `.modal` element as its show
      // transition ends, overriding any earlier focus call. Focus again once the
      // transition is finished so the trap cannot take the query field back.
      this._handleModalShown = () => this.searchInputTarget.focus();
      modal.addEventListener('shown.bs.modal', this._handleModalShown);
    } else {
      // The keys' landing spot: the search input where one renders, the listbox
      // otherwise. Without a search input the rows are plain links, not options,
      // and inside a modal the trap still wins — the document-level keydown
      // binding is what keeps the arrows working from there.
      this.#highlightHost()?.focus();
    }
  }

  disconnect() {
    const modal = this.element.closest('.modal');
    if (modal && this._handleModalShown) {
      modal.removeEventListener('shown.bs.modal', this._handleModalShown);
      this._handleModalShown = null;
    }
  }

  // Enter clicks the highlighted row's own anchor, so every row shape activates
  // exactly as a mouse click does. Bound at document level, not on the search
  // input: Bootstrap's focus trap parks focus on the `.modal` element above this
  // controller's root, so keydowns raised there never reach a listener inside it.
  keydown(event) {
    // Mid-composition keystrokes belong to the IME, not to us (the same guard
    // ai_chat_form_controller.js applies).
    if (event.isComposing) return;

    // Bootstrap drops `show` when a hide begins and tears the modal down only
    // after the fade, so Escape-then-Enter would otherwise open the row the user
    // just cancelled. A palette rendered as a full page has no modal at all.
    const modal = this.element.closest('.modal');
    if (modal && !modal.classList.contains('show')) return;

    // Must not preventDefault: the browser's own text insertion is what puts the
    // character into the field this just focused.
    if (this.hasSearchInputTarget && this.#shouldRouteToSearch(event)) {
      this.searchInputTarget.focus();
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#move(-1);
        break;
      case 'Enter':
        // A focused control keeps its own Enter: a row anchor would fire twice,
        // and the close button not at all.
        if (
          event.target.closest &&
          event.target.closest('a, button, [role="button"]')
        ) {
          return;
        }

        // The first-row fallback is grounded in a typed query; with no search
        // box there is no query, so a bare Enter is not this controller's to
        // claim (before preventDefault: the keypress stays fully untouched).
        if (this.highlightedIndex < 0 && !this.hasSearchInputTarget) return;

        // Never let the query fall back on a surrounding form's implicit submit.
        event.preventDefault();
        this.#activateHighlighted();
        break;
    }
  }

  filter() {
    const query = this.searchInputTarget.value.toLowerCase().trim();

    this.actionTargets.forEach((action) => {
      const visible =
        query === ''
          ? this.#inInitialList(action)
          : this.#matches(action, query);
      this.#setVisible(action, visible);
    });

    this.#updateCategoryVisibility();

    const visiblePages = this.pageTargets.filter(
      (page) => query !== '' && this.#matches(page, query)
    );
    this.pageTargets.forEach((page) => {
      this.#setVisible(page, visiblePages.includes(page));
    });

    if (this.hasPagesGroupTarget) {
      this.#setVisible(this.pagesGroupTarget, visiblePages.length > 0);
    }

    // "No actions available" describes the curated list, so a query's outcome is
    // the page rows or the no-results row instead.
    if (this.hasEmptyStateTarget) {
      this.#setVisible(this.emptyStateTarget, query === '');
    }

    // Only a query can come up empty: with none typed, the curated list speaks
    // for itself through the empty state.
    if (this.hasNoResultsTarget) {
      const nothingVisible =
        query !== '' &&
        !this.#hasVisibleActions(this.element) &&
        visiblePages.length === 0;
      this.#setVisible(this.noResultsTarget, nothingVisible);
    }

    // The candidate list changed shape, so every query starts unhighlighted.
    this.#clearHighlight();
  }

  // Routes a character (or Backspace) into the query when it landed somewhere
  // that is not a field — usually the dialog element holding the trapped focus.
  // Space is the exception: it activates a focused button or link, so routing it
  // away stops the modal's close button working.
  #shouldRouteToSearch(event) {
    if (document.activeElement === this.searchInputTarget) return false;

    const inAField =
      event.target.closest &&
      event.target.closest('input, textarea, select, [contenteditable]');
    if (inAField) return false;

    const activatesAControl =
      event.key === ' ' &&
      event.target.closest &&
      event.target.closest('a, button, [role="button"]');
    if (activatesAControl) return false;

    const types =
      (event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey) ||
      event.key === 'Backspace';

    return types;
  }

  // DOM order, which is the order the arrow keys traverse.
  #rows() {
    return [...this.actionTargets, ...this.pageTargets];
  }

  #visibleRows() {
    return this.#rows().filter((row) => !row.classList.contains('is-hidden'));
  }

  // The anchor activates the row and carries the option role, not the li: it
  // stays a real, Tab-focusable link.
  #anchor(row) {
    return row.querySelector('a');
  }

  #identifyRows() {
    this.#rows().forEach((row, index) => {
      const anchor = this.#anchor(row);
      if (!anchor) return;

      anchor.setAttribute('role', 'option');
      if (!anchor.id) anchor.id = `quick-actions-option-${index}`;
    });
  }

  // Holds aria-activedescendant and the focus the arrows return to: the combobox
  // where one renders, the listbox itself otherwise.
  #highlightHost() {
    if (this.hasSearchInputTarget) return this.searchInputTarget;
    return this.hasResultsTarget ? this.resultsTarget : null;
  }

  // The host is a stop in the cycle, not something the list wraps around:
  // stepping off either end returns to it with nothing highlighted.
  #move(step) {
    const rows = this.#visibleRows();
    if (rows.length === 0) return;

    if (this.highlightedIndex < 0) {
      this.#highlight(rows, step > 0 ? 0 : rows.length - 1);
      return;
    }

    const index = this.highlightedIndex + step;
    if (index < 0 || index >= rows.length) {
      this.#clearHighlight();
      // Arrows arrive from wherever focus sits (usually the dialog element), so
      // the landing spot has to be handed back explicitly.
      this.#highlightHost()?.focus();
      return;
    }

    this.#highlight(rows, index);
  }

  #highlight(rows, index) {
    rows.forEach((row, position) => {
      const active = position === index;
      row.classList.toggle('is-active', active);

      const rowAnchor = this.#anchor(row);
      if (!rowAnchor) return;
      if (active) rowAnchor.setAttribute('aria-selected', 'true');
      else rowAnchor.removeAttribute('aria-selected');
    });
    this.highlightedIndex = index;

    const anchor = this.#anchor(rows[index]);
    if (anchor) {
      this.#highlightHost()?.setAttribute('aria-activedescendant', anchor.id);
    }
    rows[index].scrollIntoView({ block: 'nearest' });
  }

  // Enter without a highlight takes the first visible row, the palette
  // convention — reachable only where a search input exists (keydown returns
  // early otherwise).
  #activateHighlighted() {
    const rows = this.#visibleRows();
    const row =
      this.highlightedIndex < 0 ? rows[0] : rows[this.highlightedIndex];
    if (!row) return;

    const anchor = this.#anchor(row);
    if (anchor) anchor.click();
  }

  #clearHighlight() {
    this.highlightedIndex = -1;
    this.#rows().forEach((row) => {
      row.classList.remove('is-active');
      this.#anchor(row)?.removeAttribute('aria-selected');
    });

    this.#highlightHost()?.removeAttribute('aria-activedescendant');
  }

  // Clearing the query restores what the server rendered, not everything: a row
  // marked `data-initial="false"` (a VIP outside the importance-ordered
  // list) is search-only. Rows with no marking are always part of the list.
  #inInitialList(action) {
    return action.dataset.initial !== 'false';
  }

  #matches(element, query) {
    return (element.dataset.searchable || '').includes(query);
  }

  // A category header must not outlive its rows: derive its visibility from
  // the action rows it contains. Called on connect (to hide categories whose
  // every row is search-only) and again on every filter keystroke.
  #updateCategoryVisibility() {
    this.categoryTargets.forEach((category) => {
      this.#setVisible(category, this.#hasVisibleActions(category));
    });
  }

  #hasVisibleActions(container) {
    return this.actionTargets.some(
      (action) =>
        container.contains(action) && !action.classList.contains('is-hidden')
    );
  }

  #setVisible(element, visible) {
    element.classList.toggle('is-hidden', !visible);
  }
}
